import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import head from 'lodash.head';
import { isAfter, isBefore, isWithinInterval } from 'date-fns';
import { GRADE, THREE_GRADES } from '../../constants/shared/GradesConstants';
import { getPercentageSign, hasMeasureGradeNoExceptions } from './tableUtils';
import { NA, PERIOD } from '../../constants/shared/CommonConstants';
import { MEASURE } from '../../constants/shared/MeasuresConstants';
import { AT_RISK } from '../../constants/reading/ApiConstants';

const { SUMMARY, BOY, MOY, EOY, BOY_MOY, MOY_EOY, PM, SUMMER, EOY_SUMMER, AFTER_SUMMER } = PERIOD;
const { TRC, OVERALL } = MEASURE;
const { PREKINDERGARTEN, ALL_GRADES } = GRADE;

export const compareDataByMeasureYearGradePeriod = (data, measure, year, grade, period) =>
    (measure ? data.measure === measure : true) &&
    (year ? data.schoolYear === year : true) &&
    (grade ? data.grade === grade : true) &&
    (period ? data.period === period : true);

export const isStatusCompleted = status =>
    !isNil(status) && !isNil(status.score) && status.isComplete;

export const getGoalByMeasureGradePeriod = (
    interps,
    measure,
    grade,
    period,
    specificGoal = null,
) => {
    if (!hasMeasureGradeNoExceptions(measure, grade, period)) return NA;

    const interpObject = interps.find(interp =>
        compareDataByMeasureYearGradePeriod(interp, measure, null, grade, period),
    );
    if (!isEmpty(interpObject)) {
        return interpObject.goal;
    }

    return specificGoal;
};

export const getStudentResultByMeasureYearGradePeriod = (results, measure, year, grade, period) =>
    (results || []).find(result =>
        compareDataByMeasureYearGradePeriod(result, measure, year, grade, period),
    );

export const getStudentResultsByMeasureYearGradePeriod = (results, measure, year, grade, period) =>
    (results || []).filter(result =>
        compareDataByMeasureYearGradePeriod(result, measure, year, grade, period),
    );

export const getStudentsByGrade = (students, grade) =>
    get(
        students.find(studentsObject => studentsObject.grade === grade),
        'students',
        [],
    );

export const getStudentListOfGradeYears = (gradeYears = []) =>
    gradeYears
        .filter(({ grade }) => grade !== PREKINDERGARTEN)
        .slice(-THREE_GRADES)
        .map(({ grade }) => grade);

export const getSelectedTabId = (navTabs, pathName) =>
    get(
        navTabs.find(navTab => navTab.path.includes(pathName)),
        'id',
        head(navTabs).id,
    );

export const isStudentHasUnofficialResults = (statuses, year, selectedPeriod) => {
    const studentTRCResults = statuses.filter(
        result => result.measure === TRC && result.schoolYear === year,
    );

    return studentTRCResults.some(
        result =>
            result.isUnofficial && (selectedPeriod === SUMMARY || result.period === selectedPeriod),
    );
};

export const getStudentGoalByMeasureGradePeriod = (
    interps,
    measure,
    grade,
    period,
    measuresWithPercentage,
) => {
    const interpObject = interps.find(interp =>
        compareDataByMeasureYearGradePeriod(interp, measure, null, grade, period),
    );
    if (!isEmpty(interpObject)) {
        return `${interpObject.goal}${getPercentageSign(measuresWithPercentage, measure)}`;
    }
    return 'N/A';
};

/**
 * Get pm statuses by PM or benchmark period based on window dates
 * @param {Array} statuses student statuses
 * @param {String} period PM or benchmark period (e.g. BOY_MOY, BOY)
 * @param {Object} windowDates window dates
 */
export const getPmStatusesByPeriod = (
    statuses,
    period,
    windowDates,
    toyPeriods = [BOY, MOY, EOY],
) => {
    // Update the default calculation of PM statuses for EOY and SUMMER periods
    // if the institution has a Summer period.
    if (toyPeriods.includes(SUMMER)) {
        if (period === EOY || period === EOY_SUMMER) {
            return statuses.filter(
                status =>
                    status.period === PM &&
                    isWithinInterval(new Date(status.date), {
                        start: new Date(windowDates[EOY].startDate),
                        end: new Date(windowDates[SUMMER].startDate),
                    }),
            );
        }

        if (period === SUMMER || period === AFTER_SUMMER) {
            return statuses.filter(
                status =>
                    status.period === PM &&
                    isAfter(new Date(status.date), new Date(windowDates[SUMMER].startDate)),
            );
        }
    }

    switch (period) {
        case BOY_MOY:
        case BOY:
            return statuses.filter(
                status =>
                    status.period === PM &&
                    isBefore(new Date(status.date), new Date(windowDates[MOY].startDate)),
            );
        case MOY_EOY:
        case MOY:
            return statuses.filter(
                status =>
                    status.period === PM &&
                    isWithinInterval(new Date(status.date), {
                        start: new Date(windowDates[MOY].startDate),
                        end: new Date(windowDates[EOY].startDate),
                    }),
            );
        default:
            return statuses.filter(
                status =>
                    status.period === PM &&
                    isAfter(new Date(status.date), new Date(windowDates[EOY].startDate)),
            );
    }
};
export const compareStatusByMeasureYearPeriodGrade = (status, measure, year, period, grade) =>
    status.schoolYear === year &&
    (isNil(period) ? true : status.period === period) &&
    (grade === ALL_GRADES ? true : status.grade === grade) &&
    status.measure === measure;

export const getGoalByMeasureYearGradePeriod = (goals, measure, year, grade, period) =>
    get(getStudentResultByMeasureYearGradePeriod(goals, measure, year, grade, period), 'goal');

export const getStudentByMeasureYearPeriodGrade = (
    results,
    studentId,
    measure,
    year,
    period,
    grade,
) => {
    return get(results[studentId], 'statuses', []).find(status =>
        compareStatusByMeasureYearPeriodGrade(status, measure, year, period, grade),
    );
};

export const getStudentStatusByYearGrade = (results, studentId, year, grade) =>
    results[studentId].statuses.find(
        status =>
            status.schoolYear === year && (grade === ALL_GRADES ? true : status.grade === grade),
    );

export const getMeasureInterpsFromResults = (results, measureKey) =>
    results.filter(result => result.measure === measureKey).map(result => result.interp);

export const getInterpsForPeriodFromResults = (results, period) =>
    results.filter(result => result.period === period).map(result => result.interp);

export const isDyslexiaRiskDisplayed = (statuses, year, selectedPeriod, currentPeriod) => {
    const activePeriod = selectedPeriod === SUMMARY ? currentPeriod : selectedPeriod;
    const overallStatus = getStudentResultByMeasureYearGradePeriod(
        statuses,
        OVERALL,
        year,
        null,
        activePeriod,
    );
    return overallStatus ? overallStatus.dyslexiaRiskLevel === AT_RISK : false;
};

export const isSummaryPercentileAvailable = (
    currentPeriod,
    selectedPeriod,
    isAfterEoy,
    isEnabledPercentileToggle,
) =>
    [BOY, MOY, EOY].includes(currentPeriod) &&
    !isAfterEoy &&
    selectedPeriod === SUMMARY &&
    isEnabledPercentileToggle;

// looks for at least 1 secure result
export const findSecure = data =>
    data?.some(({ students }) =>
        students?.some(({ columns }) => columns?.some(({ status }) => status?.isSecure)),
    );
